import { useState } from "react";

import { useMutation } from "../api/hooks";
import { Modal, Switch, useToast } from "./ui";

export interface Hour {
  weekday: number;
  open_time: string;
  close_time: string;
}

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

type Row = { enabled: boolean; open: string; close: string };

/** Opening hours per weekday (0 = Sunday, 02 §3). One PUT replaces the set. */
export function HoursEditor({ vendorId, hours, onClose, onSaved }: { vendorId: string; hours: Hour[]; onClose: () => void; onSaved: () => void }) {
  const [rows, setRows] = useState<Row[]>(() =>
    DAYS.map((_, weekday) => {
      const h = hours.find((x) => x.weekday === weekday);
      return h ? { enabled: true, open: h.open_time.slice(0, 5), close: h.close_time.slice(0, 5) } : { enabled: false, open: "09:00", close: "21:00" };
    }),
  );
  const { run, busy } = useMutation();
  const toast = useToast();

  function set(i: number, patch: Partial<Row>) {
    setRows((r) => r.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  }

  async function save() {
    const body = {
      hours: rows.flatMap((r, weekday) => (r.enabled ? [{ weekday, open_time: `${r.open}:00`, close_time: `${r.close}:00` }] : [])),
    };
    const res = await run("/vendor/hours", { method: "PUT", body, query: { vendor_id: vendorId } });
    if (res.ok) {
      toast("Opening hours saved");
      onSaved();
    } else toast(res.error, "err");
  }

  return (
    <Modal title="Opening hours" onClose={onClose}>
      <p className="muted">Days switched off are closed. No rows at all means always open.</p>
      <table className="table table-plain">
        <tbody>
          {rows.map((r, i) => (
            <tr key={DAYS[i]}>
              <td style={{ width: 130 }}>
                <Switch checked={r.enabled} onChange={(v) => set(i, { enabled: v })} label={DAYS[i]} />
              </td>
              <td>
                <input className="input" type="time" value={r.open} disabled={!r.enabled} onChange={(e) => set(i, { open: e.target.value })} />
              </td>
              <td className="muted">to</td>
              <td>
                <input className="input" type="time" value={r.close} disabled={!r.enabled} onChange={(e) => set(i, { close: e.target.value })} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="form-actions">
        <button type="button" className="btn btn-ghost" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="btn btn-primary" disabled={busy} onClick={save}>
          {busy ? "Saving…" : "Save hours"}
        </button>
      </div>
    </Modal>
  );
}
